import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useApiClient } from '@/api/ApiClientProvider'
import type { CampaignWebsite } from '@/api/campaigns'
import { META_CALLBACK_ERRORS } from '@/api/integrations'
import type {
  MetaAdAccountList,
  MetaImportRun,
  MetaIntegrationState,
  MetaMappingResult,
} from '@/api/types'
import { Badge, Card, EmptyState, LoadingBlock, formatRelativeTime, useNow } from '@/design'
import { ApiError } from '@/lib/api'

import { integrationKeys } from '../queryKeys'
import '../integrations.css'

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Integrations › Meta: connect the tenant's Meta ad accounts, choose which
 * website each one feeds, and see when the connection needs a person.
 *
 * Connecting leaves for Facebook in this tab and comes back here with
 * `?connected=1` or `?error=<code>`; the code becomes an instruction, never a
 * bare "failed". The token itself never reaches the browser.
 *
 * The card answers two different questions, and keeps them apart: is the connection
 * alive (its status and `lastError`, which a person fixes by reconnecting), and is
 * the data current (`lastImports` and `providerLag`, which nobody can fix by
 * reconnecting). Showing one as the other is how somebody ends up reconnecting a
 * healthy integration because a CronJob was late.
 */
export function MetaIntegrationPage() {
  const client = useApiClient()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const stateKey = integrationKeys.meta(client.kind)

  const state = useQuery({ queryKey: stateKey, queryFn: () => client.metaIntegration() })

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: integrationKeys.metaAll })
    await queryClient.invalidateQueries({ queryKey: integrationKeys.adAccountsAll })
    await queryClient.invalidateQueries({ queryKey: integrationKeys.summaryAll })
  }

  const connect = useMutation({
    mutationFn: () => client.connectMeta(),
    onSuccess: async ({ authorizeUrl, completesInApp }) => {
      if (completesInApp) {
        // The demo client connects at once and lands where the callback would.
        await refresh()
        navigate(authorizeUrl)
      } else {
        window.location.assign(authorizeUrl)
      }
    },
  })
  const disconnect = useMutation({
    mutationFn: () => client.disconnectMeta(),
    onSuccess: (next) => {
      queryClient.setQueryData(stateKey, next)
      void refresh()
    },
  })
  const runNow = useMutation({
    mutationFn: () => client.runMetaImportNow(),
    onSuccess: (next) => {
      queryClient.setQueryData(stateKey, next)
      void refresh()
    },
  })

  if (state.isPending) return <LoadingBlock rows={4} label="Loading the Meta connection" />
  if (state.isError) return <ErrorText error={state.error} fallback="The Meta connection could not be loaded." />

  const meta = state.data
  if (!meta.configured) return <NotConfigured meta={meta} />

  const busy = connect.isPending || disconnect.isPending || runNow.isPending
  const onConnect = () => connect.mutate()
  const onDisconnect = () => {
    const message =
      'Disconnect Meta? Spend and campaign results stop updating until someone connects again. Data already imported is kept.'
    if (window.confirm(message)) disconnect.mutate()
  }

  return (
    <div className="ci-integration">
      <CallbackNotice connected={params.get('connected') === '1'} error={params.get('error')} status={meta.status} />
      {meta.status === 'reconnect_required' ? (
        <div role="alert" className="ci-integration__banner">
          <strong>Meta needs reconnecting.</strong>
          <span>{meta.lastError ?? 'Meta no longer accepts this connection.'}</span>
          <span>Until it is reconnected, Meta spend and results stop updating. Nothing already imported is lost.</span>
          <span>
            <button type="button" className="ci-button" data-variant="primary" disabled={busy} onClick={onConnect}>
              {connect.isPending ? 'Opening Facebook…' : 'Reconnect Meta'}
            </button>
          </span>
        </div>
      ) : null}

      <ConnectionCard meta={meta} busy={busy} connecting={connect.isPending} onConnect={onConnect} onDisconnect={onDisconnect} />
      <ErrorText error={connect.error ?? disconnect.error} />

      {meta.status !== 'not_connected' ? (
        <ImportsCard
          meta={meta}
          busy={busy}
          running={runNow.isPending}
          error={runNow.error}
          onRunNow={() => runNow.mutate()}
        />
      ) : null}

      {meta.status === 'connected' ? <AdAccountsCard meta={meta} onSaved={refresh} /> : null}
      {meta.status === 'reconnect_required' && meta.adAccounts.length > 0 ? (
        <Card title="Ad accounts" subtitle="Mapping is kept while the connection is reconnected.">
          <ul className="ci-integration__list">
            {meta.adAccounts.map((account) => (
              <li key={account.adAccountId}>
                {account.name ?? account.externalAdAccountId} — {account.currency}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  )
}

function NotConfigured({ meta }: { meta: MetaIntegrationState }) {
  return (
    <Card title="Meta">
      <EmptyState
        variant="disconnected"
        title="Not configured on this environment"
        description="Meta cannot be connected here yet: TmT support has to finish setting it up. Nothing on your side needs to change."
      />
      {meta.missingConfiguration.length > 0 ? (
        <p className="ci-integration__note">
          For TmT support — missing: <code>{meta.missingConfiguration.join(', ')}</code>
        </p>
      ) : null}
    </Card>
  )
}

function CallbackNotice({
  connected,
  error,
  status,
}: {
  connected: boolean
  error: string | null
  status: MetaIntegrationState['status']
}) {
  if (error) {
    return (
      <p role="alert" className="ci-integration__error">
        {META_CALLBACK_ERRORS[error] ?? META_CALLBACK_ERRORS.meta_refused}
      </p>
    )
  }
  if (connected && status === 'connected') {
    return (
      <p role="status" className="ci-integration__ok">
        Meta is connected. Choose which website each ad account feeds below.
      </p>
    )
  }
  return null
}

function ConnectionCard({
  meta,
  busy,
  connecting,
  onConnect,
  onDisconnect,
}: {
  meta: MetaIntegrationState
  busy: boolean
  connecting: boolean
  onConnect: () => void
  onDisconnect: () => void
}) {
  if (meta.status === 'not_connected') {
    return (
      <Card title="Connect Meta" subtitle="Read-only access to your ad accounts' spend, campaigns and results.">
        <p className="ci-integration__note">
          You will be sent to Facebook to approve access, then brought back here. Sign in to Facebook as someone with a
          role on your ad accounts, and keep the <strong>ads_read</strong> permission ticked. Competition Insights can
          only read your ads; it can never change or create them.
        </p>
        <span className="ci-integration__actions">
          <button type="button" className="ci-button" data-variant="primary" disabled={busy} onClick={onConnect}>
            {connecting ? 'Opening Facebook…' : 'Connect Meta'}
          </button>
        </span>
      </Card>
    )
  }
  const connected = meta.status === 'connected'
  return (
    <Card
      title="Meta connection"
      actions={<Badge tone={connected ? 'positive' : 'warning'}>{connected ? 'Connected' : 'Reconnect required'}</Badge>}
    >
      <dl className="ci-integration__facts">
        <dt>Connected as</dt>
        <dd>{meta.connectedUserName ?? 'A Facebook user'}</dd>
        <dt>Connected on</dt>
        <dd>{formatDate(meta.connectedAt)}</dd>
        <dt>Access</dt>
        <dd>{meta.scopes.length > 0 ? meta.scopes.join(', ') : 'None'}</dd>
        <dt>Expires</dt>
        <dd>
          <Expiry expiresAt={meta.tokenExpiresAt} refreshWindowDays={meta.refreshWindowDays} />
        </dd>
        <dt>Last checked</dt>
        <dd>{formatDate(meta.lastCheckedAt)}</dd>
      </dl>
      <span className="ci-integration__actions">
        <button type="button" className="ci-button" data-variant="ghost" disabled={busy} onClick={onDisconnect}>
          Disconnect
        </button>
      </span>
    </Card>
  )
}

/**
 * What has been imported, how fresh it is, and the button that does it now.
 *
 * "Provider data is delayed" is deliberately not phrased as a fault: the connection
 * is fine, and there is nothing for the person to fix — it says what it means for the
 * numbers and leaves it there.
 */
function ImportsCard({
  meta,
  busy,
  running,
  error,
  onRunNow,
}: {
  meta: MetaIntegrationState
  busy: boolean
  running: boolean
  error: unknown
  onRunNow: () => void
}) {
  const now = useNow(60_000)
  const { hierarchy, insights } = meta.lastImports
  return (
    <Card
      title="Imports"
      subtitle="Spend and campaign results are imported on a schedule; nothing here needs doing by hand."
      actions={meta.providerLag ? <Badge tone="warning">Delayed</Badge> : null}
    >
      {meta.providerLag ? (
        <p role="status" className="ci-integration__note">
          <strong>Provider data is delayed.</strong> Meta spend has not refreshed as often as it
          should, so today&rsquo;s figures may be behind. The connection is fine and nothing needs
          reconnecting; the next import picks it up.
        </p>
      ) : null}
      <dl className="ci-integration__facts">
        <dt>Last campaign import</dt>
        <dd>
          <ImportState run={hierarchy} now={now} />
        </dd>
        <dt>Last spend import</dt>
        <dd>
          <ImportState run={insights} now={now} />
        </dd>
      </dl>
      <span className="ci-integration__actions">
        <button type="button" className="ci-button" disabled={busy} onClick={onRunNow}>
          {running ? 'Importing…' : 'Run import now'}
        </button>
      </span>
      <ErrorText error={error} fallback="The import could not be started." />
    </Card>
  )
}

/** One run: when, whether it worked, how much it wrote, and our wording of a failure. */
function ImportState({ run, now }: { run: MetaImportRun | null; now: Date }) {
  if (!run) return <>Not yet — the next scheduled import is its first</>
  if (run.status === 'running') return <>Running now</>
  const when = formatRelativeTime(run.finishedAt ?? run.startedAt, now)
  if (run.status === 'failed') {
    return (
      <>
        <Badge tone="critical">Failed</Badge> {when}
        {run.error ? <small className="ci-integration__id">{run.error}</small> : null}
      </>
    )
  }
  return (
    <>
      {when} — {run.rowsWritten === 1 ? '1 row' : `${run.rowsWritten} rows`}
    </>
  )
}

/** "Checked daily" is only true while the refresh window is still ahead of the expiry. */
function Expiry({ expiresAt, refreshWindowDays }: { expiresAt: string | null; refreshWindowDays: number }) {
  if (!expiresAt) return <>Does not expire</>
  const days = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / DAY_MS)
  return (
    <>
      {formatDate(expiresAt)}
      {days > refreshWindowDays ? ' — checked daily; you will be asked to reconnect in good time if Meta will not extend it' : null}
    </>
  )
}

function AdAccountsCard({ meta, onSaved }: { meta: MetaIntegrationState; onSaved: () => Promise<void> }) {
  const client = useApiClient()
  const accounts = useQuery({ queryKey: integrationKeys.adAccounts(client.kind), queryFn: () => client.metaAdAccounts() })
  const websites = useQuery({ queryKey: integrationKeys.websites(client.kind), queryFn: () => client.campaignWebsites() })

  return (
    <Card title="Ad accounts" subtitle="Choose the website each ad account's spend belongs to. Unmapped accounts are not imported.">
      {accounts.isPending || websites.isPending ? (
        <LoadingBlock rows={3} label="Loading your ad accounts" />
      ) : accounts.isError ? (
        <ErrorText error={accounts.error} fallback="Your ad accounts could not be loaded." />
      ) : websites.isError ? (
        <ErrorText error={websites.error} fallback="Your websites could not be loaded." />
      ) : accounts.data.items.length === 0 ? (
        <EmptyState
          variant="no-data"
          title="No ad accounts"
          description={`${meta.connectedUserName ?? 'The connected Facebook user'} has no role on any ad account. Ask an admin of the ad account to add them in Meta Business Settings (Users › People), then reload this page.`}
        />
      ) : (
        <MappingForm
          key={accounts.dataUpdatedAt}
          list={accounts.data}
          websites={websites.data}
          onSave={async (mappings) => {
            const result = await client.saveMetaAdAccounts(mappings)
            await onSaved()
            return result
          }}
        />
      )}
    </Card>
  )
}

function MappingForm({
  list,
  websites,
  onSave,
}: {
  list: MetaAdAccountList
  websites: CampaignWebsite[]
  onSave: (mappings: { externalAdAccountId: string; websiteId: string }[]) => Promise<MetaMappingResult>
}) {
  const [choice, setChoice] = useState<Record<string, string>>(() =>
    Object.fromEntries(list.items.map((item) => [item.externalAdAccountId, item.websiteId ?? ''])),
  )
  const save = useMutation({
    mutationFn: () =>
      onSave(
        Object.entries(choice)
          .filter(([, websiteId]) => websiteId !== '')
          .map(([externalAdAccountId, websiteId]) => ({ externalAdAccountId, websiteId })),
      ),
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    save.mutate()
  }

  return (
    <form aria-label="Ad account mapping" onSubmit={submit} className="ci-integration__mapping">
      <div className="ci-table-wrap">
        <table className="ci-table">
          <thead>
            <tr>
              <th scope="col">Ad account</th>
              <th scope="col">Currency</th>
              <th scope="col">Status</th>
              <th scope="col">Website</th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((item) => {
              const label = item.name ?? item.externalAdAccountId
              return (
                <tr key={item.externalAdAccountId}>
                  <th scope="row">
                    {label}
                    <small className="ci-integration__id">{item.accountId}</small>
                  </th>
                  <td>
                    {item.currency}{' '}
                    {item.currencyMatchesTenant ? null : <Badge tone="warning">Differs from {list.tenantCurrency}</Badge>}
                  </td>
                  <td>{item.accountStatus ?? '—'}</td>
                  <td>
                    <select
                      aria-label={`Website for ${label}`}
                      value={choice[item.externalAdAccountId] ?? ''}
                      disabled={save.isPending}
                      onChange={(event) => setChoice({ ...choice, [item.externalAdAccountId]: event.target.value })}
                    >
                      <option value="">Not mapped</option>
                      {websites.map((website) => (
                        <option key={website.id} value={website.id}>
                          {website.name} ({website.environment})
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <span className="ci-integration__actions">
        <button type="submit" className="ci-button" data-variant="primary" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save mapping'}
        </button>
      </span>
      <ErrorText error={save.error} fallback="The mapping could not be saved." />
      {save.data ? <SaveResult result={save.data} /> : null}
    </form>
  )
}

function SaveResult({ result }: { result: MetaMappingResult }) {
  const count = result.items.length
  return (
    <div role="status" className="ci-integration__ok">
      <p>
        Saved. {count === 1 ? '1 ad account feeds' : `${count} ad accounts feed`} a website
        {result.unmapped.length > 0 ? `; ${result.unmapped.length} no longer ${result.unmapped.length === 1 ? 'does' : 'do'}` : ''}.
      </p>
      {result.warnings.length > 0 ? (
        <ul className="ci-integration__warnings">
          {result.warnings.map((warning) => (
            <li key={warning.externalAdAccountId}>{warning.message}</li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function ErrorText({ error, fallback }: { error: unknown; fallback?: string }) {
  if (!error) return null
  const message = error instanceof ApiError ? error.message : (fallback ?? 'Something went wrong.')
  return (
    <p role="alert" className="ci-integration__error">
      {message}
    </p>
  )
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
